/**
 * Registro de kinds de módulo de curso (F2-A) — misma forma que BlockRegistry
 * (`learning-blocks/registry.ts`): el authoring/player consultan AQUÍ el plugin
 * de cada `kind`; añadir un kind nuevo es `registry.register(plugin)` sin tocar
 * el core. Es la pieza que hace la espina Moodle extensible (F3 registra
 * assignment/forum/video/live_session sin ediciones de core).
 */

import type { FeatureManifest } from "../country/packs";
import type { CourseModulePlugin } from "./types";

export class CourseModuleRegistry<TComponent = unknown> {
  private readonly plugins = new Map<string, CourseModulePlugin<TComponent>>();

  /** Registra un plugin. Lanza si el `kind` ya existe (evita pisar kinds). */
  register(plugin: CourseModulePlugin<TComponent>): this {
    if (this.plugins.has(plugin.kind)) {
      throw new Error(`CourseModuleRegistry: kind duplicado "${plugin.kind}"`);
    }
    if (plugin.manifest.key !== `academy.module.${plugin.kind}`) {
      throw new Error(
        `CourseModuleRegistry: manifest.key "${plugin.manifest.key}" no sigue la convención "academy.module.${plugin.kind}"`,
      );
    }
    if (!plugin.manifest.name || !plugin.manifest.description) {
      throw new Error(
        `CourseModuleRegistry: manifest de "${plugin.kind}" incompleto (name/description obligatorios — ninguna feature oculta)`,
      );
    }
    this.plugins.set(plugin.kind, plugin);
    return this;
  }

  /** Registra varios de una vez. */
  registerAll(plugins: readonly CourseModulePlugin<TComponent>[]): this {
    for (const p of plugins) {
      this.register(p);
    }
    return this;
  }

  get(kind: string): CourseModulePlugin<TComponent> | undefined {
    return this.plugins.get(kind);
  }

  has(kind: string): boolean {
    return this.plugins.has(kind);
  }

  /** Kinds registrados (para el menú "añadir módulo" del authoring). */
  kinds(): string[] {
    return [...this.plugins.keys()];
  }

  /** Alias de `kinds()` — paridad con BlockRegistry.types(). */
  types(): string[] {
    return this.kinds();
  }

  /**
   * Manifests de todos los kinds (catálogo auto-descriptivo → `gen:features`).
   */
  manifests(): FeatureManifest[] {
    return [...this.plugins.values()].map((p) => p.manifest);
  }

  /**
   * Kinds habilitados según `course_settings.enabled_module_kinds` (JSON array
   * o null). `null`/inválido = TODOS los registrados; una lista filtra (solo
   * kinds realmente registrados).
   */
  enabledKinds(enabledModuleKinds: string | null | undefined): string[] {
    const all = this.kinds();
    if (!enabledModuleKinds) {
      return all;
    }
    try {
      const parsed: unknown = JSON.parse(enabledModuleKinds);
      if (!Array.isArray(parsed)) {
        return all;
      }
      const wanted = new Set(parsed.filter((k): k is string => typeof k === "string"));
      return all.filter((k) => wanted.has(k));
    } catch {
      return all;
    }
  }
}
